import React, { forwardRef, type ReactElement } from 'react';

import { FixedHolder } from '../FixedHolder';
import { getColKey } from '../Table.helpers';
import { type ColsWidths, type ColumnConfig, type TableResourceRecord } from '../Table.types';

import { TableHeadColumn } from './TableHeadColumn';

import styles from '../Table.module.scss';

import type { SORTING_MODES } from '../../../utils';

type TableHeaderProps<RecordType = any> = {
    columns: ColumnConfig<RecordType>[];
    sortKey?: string | null;
    sortMode?: SORTING_MODES;
    onSortBy?: (key: string) => void;
    withInnerScroll?: boolean;
    colsWidths: ColsWidths;
};

export const TableHeader = forwardRef<HTMLDivElement, TableHeaderProps>(
    <RecordType extends TableResourceRecord = any>(
        {
            columns,
            sortKey,
            sortMode,
            onSortBy,
            withInnerScroll,
            colsWidths
        }: TableHeaderProps<RecordType>,
        ref: React.Ref<HTMLDivElement>
    ): ReactElement => {
        const tableHeader = (
            <thead>
                <tr>
                    {columns.map((column) => (
                        <TableHeadColumn
                            key={getColKey(column)}
                            sortKey={sortKey}
                            sortMode={sortMode}
                            column={column}
                            onSortBy={onSortBy}
                        />
                    ))}
                </tr>
            </thead>
        );

        return withInnerScroll ? (
            <FixedHolder
                className={styles.tableHeader}
                ref={ref}
                columns={columns}
                colsWidths={colsWidths}
            >
                {tableHeader}
            </FixedHolder>
        ) : (
            tableHeader
        );
    }
);

TableHeader.displayName = 'TableHeader';
